import React, { useRef, useCallback, useEffect } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";
import { closeMenu } from "../../../../State/actions";
import { useDispatch } from "react-redux";

const ButtonMenuClose = ({
  onClose,
  x = 0,
  y = 0,
  isSettings = false,
  sizeMultiplier = 1.0,
}) => {
  const { t } = useTranslation();
  const { getResponsiveConfig } = useDimensions();
  const dispatch = useDispatch();
  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Get the close icon texture - use settings texture if isSettings is true
  const texture = PIXI.Assets.get(
    isSettings ? ASSET_NAME.icon_popi_close_button : ASSET_NAME.icon_close
  );

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Apply size multiplier to button dimensions (affects width/height, not scale)
  const buttonWidth = buttonConfig.width * sizeMultiplier;
  const buttonHeight = buttonConfig.height * sizeMultiplier;

  const handleClick = () => {
    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before closing
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    // Call the close callback
    if (onClose) {
      onClose();
      dispatch(closeMenu());
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={texture}
      name="secondary_button_menu_close"
      anchor={0.5}
      width={buttonWidth}
      height={buttonHeight}
      x={x}
      y={y}
      eventMode="static"
      cursor="pointer"
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
      accessibleTitle={t("close")}
      accessibleHint={t("close")}
    />
  );
};

export default ButtonMenuClose;
